import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeftRight, 
  Building2, 
  Upload,
  FileText,
  X,
  Loader2,
  AlertTriangle,
  Info
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferRequestDialog({ open, onOpenChange, existingEnterprise, onTransferRequested }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 
                          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error("Type de fichier non autorisé. Utilisez PDF, JPG, PNG ou Word.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10MB)");
      return;
    }

    setProofFile(file);
  };

  const handleSubmit = async () => {
    if (!proofFile) {
      toast.error("Veuillez joindre un document de preuve (mandat, lettre d'accord, etc.)");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("enterprise_id", existingEnterprise.enterprise.id);
      formData.append("reason", reason || "");
      formData.append("proof_document", proofFile);

      await axios.post(`${API_URL}/enterprises/request-transfer`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Demande de transfert envoyée avec succès");
      onOpenChange(false);
      setReason("");
      setProofFile(null);
      if (onTransferRequested) onTransferRequested();
    } catch (error) {
      console.error(/* PROD */ "Transfer request error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la demande de transfert");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setReason("");
    setProofFile(null);
  };

  if (!existingEnterprise) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-blue-600" />
            Demander un transfert d'entreprise
          </DialogTitle>
          <DialogDescription>
            Cette entreprise est déjà enregistrée. Vous pouvez demander son transfert vers votre cabinet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info entreprise existante */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">
                  {existingEnterprise.enterprise.raison_sociale}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  RCCM : {existingEnterprise.enterprise.rccm}
                </p>
                <p className="text-sm text-muted-foreground">
                  NIF : {existingEnterprise.enterprise.nif}
                </p>
                <p className="text-sm text-muted-foreground">
                  Région : {existingEnterprise.enterprise.region}
                </p>
              </div>
            </div>
          </div>

          {/* Info cabinet actuel */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Actuellement enregistrée chez :
                </p>
                <p className="font-semibold text-amber-900 mt-1">
                  {existingEnterprise.current_cabinet.nom}
                </p>
              </div>
            </div>
          </div>

          {/* Instructions preuve */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Document requis :</strong> Joignez une preuve d'accord de l'entreprise 
                (mandat signé, lettre officielle, ou tout document attestant que l'entreprise 
                souhaite changer de cabinet).
              </div>
            </div>
          </div>

          {/* Upload preuve */}
          <div>
            <Label className="text-sm font-medium">
              Document de preuve <span className="text-red-500">*</span>
            </Label>
            <div className="mt-2">
              {proofFile ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <span className="flex-1 text-sm text-emerald-700 truncate">
                    {proofFile.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setProofFile(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="proof-upload"
                  className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    Cliquez pour télécharger (PDF, JPG, PNG, Word - max 10MB)
                  </span>
                </label>
              )}
              <input
                id="proof-upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Motif (optionnel) */}
          <div>
            <Label htmlFor="reason" className="text-sm font-medium">
              Motif de la demande (optionnel)
            </Label>
            <Textarea
              id="reason"
              placeholder="Expliquez pourquoi vous souhaitez gérer cette entreprise..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !proofFile}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Demander le transfert
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
